import type { FlowCell, FlowRow, Round, Survival } from '../schemas/entities';
import type { SideIndex } from '../schemas/format';
import { segmentSide, sideOfSlot } from './sides';

export interface ArgumentAssessment {
  rowId: string;
  sheetId: string;
  /** First non-empty text on the row — what the argument is called on the flow. */
  title: string;
  /** Side that made the argument (owns it), or null if unknown. */
  side: SideIndex | null;
  /** For each required role (e.g. summary, final): did the owning side carry it there? */
  carried: { role: string; segmentId: string | null; present: boolean }[];
  answered: boolean;
  markedDropped: boolean;
  markedTurn: boolean;
  concededInCross: boolean;
  suggestion: Survival;
  why: string;
}

/** How to name a required speech: PF's summary / final focus, otherwise "last speech (2AR)". */
export const extensionName = (role: string, segmentId: string | null, event?: string) =>
  role === 'summary' ? 'summary' : role === 'final' ? (event === 'pf' || !event ? 'final focus' : `last speech${segmentId ? ` (${segmentId})` : ''}`) : role;

const hasContent = (c: FlowCell | undefined) => !!c && (c.text.trim().length > 0 || c.marks.includes('extended'));

/**
 * Suggest which arguments survived to the end, based only on what's on the flow.
 * This is a suggestion; the judge confirms or overrides every one.
 */
export const assessArguments = (round: Round, rows: FlowRow[], cells: FlowCell[]): ArgumentAssessment[] => {
  const f = round.format;
  const segIndex = new Map(f.segments.map((s, i) => [s.id, i]));
  const bySegment = new Map(f.segments.map((s) => [s.id, s]));
  const cellsByRow = new Map<string, FlowCell[]>();
  for (const c of cells) {
    const list = cellsByRow.get(c.rowId) ?? [];
    list.push(c);
    cellsByRow.set(c.rowId, list);
  }

  return rows
    .map((row): ArgumentAssessment | null => {
      const rowCells = (cellsByRow.get(row.id) ?? []).sort((a, b) => (segIndex.get(a.segmentId) ?? 0) - (segIndex.get(b.segmentId) ?? 0));
      const origin = rowCells.find((c) => c.text.trim().length > 0 && bySegment.get(c.segmentId)?.kind === 'speech');
      if (!origin) return null;
      const originSeg = bySegment.get(origin.segmentId)!;
      const sheet = f.sheets.find((s) => s.id === row.sheetId);
      const side = segmentSide(round, originSeg) ?? (sheet?.owner ? sideOfSlot(round, sheet.owner) : null);
      const originSlot = originSeg.by === 'both' ? null : originSeg.by;

      const carried = f.extensionRoles.map((role) => {
        const seg = f.segments.find((s) => s.role === role && s.by === originSlot);
        const cell = seg ? rowCells.find((c) => c.segmentId === seg.id) : undefined;
        return { role, segmentId: seg?.id ?? null, present: hasContent(cell) };
      });
      const answered = rowCells.some((c) => {
        const s = bySegment.get(c.segmentId);
        return s && s.kind === 'speech' && s.by !== originSlot && c.text.trim().length > 0;
      });
      const markedDropped = rowCells.some((c) => c.marks.includes('dropped'));
      const markedTurn = rowCells.some((c) => c.marks.includes('turn'));
      const concededInCross = rowCells.some((c) => bySegment.get(c.segmentId)?.kind === 'questioning' && c.marks.includes('conceded'));
      const allCarried = carried.length > 0 && carried.every((c) => c.present);
      const title = row.title ?? origin.text.trim().split('\n')[0]!.slice(0, 80);

      let suggestion: Survival;
      let why: string;
      if (originSlot === null) {
        suggestion = 'unsure';
        why = 'Could not tell who made this argument.';
      } else if (markedDropped) {
        suggestion = 'dead';
        why = 'You marked it dropped.';
      } else if (allCarried) {
        suggestion = 'alive';
        why = `Extended through ${carried.map((c) => extensionName(c.role, c.segmentId, f.event)).join(' and ')}.`;
      } else if (carried.some((c) => c.present)) {
        suggestion = 'unsure';
        const missing = carried.filter((c) => !c.present).map((c) => extensionName(c.role, c.segmentId, f.event));
        why = `Not in ${missing.join(' or ')} on your flow.${f.extensionNote ? ` ${f.extensionNote}` : ''}`;
      } else {
        suggestion = 'dead';
        why = 'Never extended past the early speeches.';
      }
      return { rowId: row.id, sheetId: row.sheetId, title, side, carried, answered, markedDropped, markedTurn, concededInCross, suggestion, why };
    })
    .filter((a): a is ArgumentAssessment => a !== null);
};
